import { CommandEmpty } from "@corpuscomplete/web-ui/components/command";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Item, ItemActions, ItemMedia } from "@corpuscomplete/web-ui/components/item";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Input } from "@corpuscomplete/web-ui/components/input";

import { type FoodItemInput, FoodLogOnDate } from "@corpuscomplete/core-ui/nutrition/food-log";

import { NutrientAmountsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { NutrientsProgress } from "@corpuscomplete/web/components/nutrition/nutrient-progress";
import { AlertCircle } from "lucide-react";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { FoodSuggestionsCommandGroup } from "@corpuscomplete/web/components/nutrition/food-search";
import type { Food } from "@corpuscomplete/core/nutrition/food";
import { FoodItemInputView } from "@corpuscomplete/web/components/nutrition/food-item";
import { FoodSearchDialog } from "@corpuscomplete/web/components/nutrition/food-search-dialog";

type FoodLogOnDateDialogProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Add-entry dialog implementation for FoodLogOnDate.
 * Contract: consumes dialog state/actions from the core-ui store and
 * only owns search rendering, item editing UI, and local close behavior.
 */
export function FoodLogOnDateDialog({ open, onClose }: FoodLogOnDateDialogProps) {
  const searchState = FoodSearchUI.useSearchState();
  const onAddItem = FoodLogOnDate.useAddEntryDialogStore((state) => state.onAddItem);
  const { userId } = FoodLogOnDate.useContext();
  const time = FoodLogOnDate.useAddEntryDialogStore((state) => state.time);
  const { suggestions, isLoading: suggestionsLoading } = FoodSearchUI.useSuggestions({
    userId,
    time,
  });

  function onSelectItem(item: Food.Item) {
    onAddItem(item);
    searchState.onChangeText("");
  }

  return (
    <FoodSearchDialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      searchState={searchState}
      onSelectItem={onSelectItem}
      className="sm:max-w-4xl sm:h-[28rem]"
      placeholder="Search for foods to log"
      renderShortQueryContent={() =>
        suggestionsLoading ? (
          <CommandEmpty>Loading...</CommandEmpty>
        ) : suggestions.length > 0 ? (
          <FoodSuggestionsCommandGroup suggestions={suggestions} onSelectItem={onSelectItem} />
        ) : null
      }
      renderSidePanel={() => <ItemsView />}
    />
  );
}

function ItemsView() {
  const time = FoodLogOnDate.useAddEntryDialogStore((state) => state.time);
  const onChangeTime = FoodLogOnDate.useAddEntryDialogStore((state) => state.onChangeTime);
  const items = FoodLogOnDate.useAddEntryDialogStore((state) => state.items);
  const onLog = FoodLogOnDate.useAddEntryDialogStore((state) => state.onLog);

  const nutrientAmounts = FoodLogOnDate.useAddEntryDialogNutrientAmounts();
  const programDay = FoodLogOnDate.useProgramDay();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {programDay ? (
        <NutrientsProgress
          targets={programDay.nutrientTargets}
          actual={{ amounts: nutrientAmounts }}
          className="p-2"
        />
      ) : (
        <NutrientAmountsLabel amounts={nutrientAmounts} className="p-2" />
      )}

      <div className="flex flex-col overflow-y-auto px-2">
        {items.length === 0 && (
          <Item>
            <ItemMedia>
              <AlertCircle className="size-4" />
            </ItemMedia>
            <Text variant="sm">No items</Text>
            <ItemActions>
              <Button variant="ghost" size="sm">
                <Text variant="sm">Add Item</Text>
              </Button>
            </ItemActions>
          </Item>
        )}
        {items.map((item, index) => (
          <ItemView key={index} item={item} index={index} />
        ))}
      </div>

      <div className="flex-1" />

      <div className="flex flex-row justify-end gap-1 border-t bg-background p-1">
        <Input
          type="time"
          className="w-auto"
          value={time.toString({
            smallestUnit: "minute",
          })}
          onChange={(e) => onChangeTime(Temporal.PlainTime.from(e.target.value))}
        />

        <Button onClick={onLog}>
          <Text variant="sm">Log Foods</Text>
        </Button>
      </div>
    </div>
  );
}

function ItemView({ item, index }: { item: FoodItemInput.Info; index: number }) {
  const onEditItemAmount = FoodLogOnDate.useAddEntryDialogStore((state) => state.onEditItemAmount);
  const onEditItemServing = FoodLogOnDate.useAddEntryDialogStore(
    (state) => state.onEditItemServing,
  );
  const onRemoveItem = FoodLogOnDate.useAddEntryDialogStore((state) => state.onRemoveItem);

  return (
    <FoodItemInputView
      className="flex-1 rounded-none px-0 py-1"
      itemInput={{ amount: item.amount, foodServingId: item.serving.id }}
      onEditItemAmount={(amount) => onEditItemAmount(index, amount.toString())}
      onEditItemServing={(foodServingId) => onEditItemServing(index, foodServingId)}
      onRemoveItem={() => onRemoveItem(index)}
    />
  );
}
